import { useRouter } from 'next/router';
import { useEffect } from 'react';

const EVENT_NAME = 'virtualPageView';

/**
 * Notifica a Adobe Launch els canvis de pàgina de la navegació client-side (SPA) d'esadir.
 *
 * La property d'esadir fa marcatge implícit: dedueix el context de `document.title` i
 * `location.pathname`. `next/head` aplica el nou `<title>` dins d'un efecte de React, de manera
 * que esperem al repintat abans de disparar la crida directa; així Launch no llegeix el títol
 * de la pàgina anterior. A més, s'envien `url`, `path` i `title` al detall de l'event perquè la
 * regla els pugui llegir amb `%event.detail.title%` sense dependre del DOM.
 *
 * ⚠️ Requereix una regla "Direct Call" amb identificador `virtualPageView` a la property de
 * Launch (`f75b8874cf49`). Si la regla no existeix, `_satellite.track()` no fa res.
 */
const useVirtualPageView = () => {
    const router = useRouter();

    useEffect(() => {
        let cancelled = false;

        const track = (url) => {
            if (cancelled) return;
            window._satellite?.track(EVENT_NAME, {
                url,
                path: window.location.pathname,
                title: document.title
            });
        };

        // Dos frames: el primer deixa que React apliqui els efectes (inclòs el <title> de
        // next/head), el segon garanteix que el DOM ja està pintat.
        const afterRepaint = (callback) => {
            if (typeof window.requestAnimationFrame !== 'function') {
                window.setTimeout(callback, 0);
                return;
            }
            window.requestAnimationFrame(() => window.requestAnimationFrame(callback));
        };

        const onRouteChangeComplete = (url, options) => {
            // El routing shallow (p. ex. netejar la query del cercador) no canvia de pàgina
            if (options?.shallow) return;
            afterRepaint(() => track(url));
        };

        router.events.on('routeChangeComplete', onRouteChangeComplete);

        return () => {
            cancelled = true;
            router.events.off('routeChangeComplete', onRouteChangeComplete);
        };
    }, [router.events]);
};

export { useVirtualPageView };
